import type { AxiosError } from 'axios';
import type { VoPermitModule, VoPermitSystem } from '@/apis/profile';
import { computed, createVNode, reactive, toRefs } from 'vue';
import {
  IS_DEV,
  IS_JYH_PROD,
  IS_JYH_RESERVE,
  IS_RESERVE,
  STORE_PERMIT,
  SYS_MOUNT_POINT,
} from '@inn/const';
import { useConfirm } from '@inn/components';
import { defineStore } from 'pinia';
import { isEmpty, isMacroCase, isUrl } from '@hlx/use';
import { isArray, omit, pick } from 'radash';
import { getBaseConfigByOrigin, resolveEnvironmentVariables } from '@inn/utils';
import { XomError } from '@/utils';
import { getMyPermits } from '@/apis/profile';

const MICROS: XomStateSystem[] = [
  /**
   * 基座用户中心子系统注册配置
   */
  {
    key: 'USER_CENTER',
    activeRule: '/member',
    type: 'MAIN',
    container: SYS_MOUNT_POINT,
    label: '用户中心',
    entry: null,
  },
];

export const usePermitStore = defineStore(
  'permits',
  () => {
    const states = reactive<XomStatePermits>({
      matched: false,
      sites: [],
      systems: [],
      modules: [],
      features: [],
    });

    const authed = computed(() => !!states.sites?.length && !!states.systems?.length);

    /**
     * 标记权限已经完成校验，路由守卫的后续执行无需再请求权限
     */
    const markMatched = () => {
      states.matched = true;
    };

    /**
     * 配置某个子系统的开发入口
     *
     * @param payload - 子系统的权限点配置
     */
    const regDevEntry = (payload: VoPermitSystem) => {
      if (!IS_DEV) {
        return;
      }
      const oldIdx = states.systems.findIndex((s_) => s_.activeRule === payload.activeRule);
      const _old = states.systems[oldIdx];
      if (_old?.type === 'MAIN') {
        return;
      }

      const parsed_ = parseVoToSystem({
        ...payload,
        path: _old ? _old.entry : payload.path,
        devEntry: payload.path,
        sort: _old?.sort || 9999,
      });
      if (~oldIdx) {
        states.systems.splice(oldIdx, 1, parsed_);
      } else {
        states.systems.push(parsed_);
      }
    };

    /**
     * 移除某个子系统的开发入口
     *
     * @param activeRule - 子系统的唯一标识
     */
    const rmvDevEntry = (activeRule: string) => {
      const idx = states.systems.findIndex((s_) => s_.activeRule === activeRule);
      if (~idx) {
        states.systems.splice(idx, 1, omit(states.systems[idx], ['devEntry']));
      }
    };

    /**
     * 刷新权限数据
     */
    const reload = async () => {
      let _res: Awaited<ReturnType<typeof getMyPermits>>;
      try {
        _res = await getMyPermits();
      } catch (error) {
        clear();
        throw new XomError(error as AxiosError);
      }
      const { pages, buttons: features, sites } = _res;

      if (!Array.isArray(sites) || sites.length === 0) {
        clear();
        throw new XomError({ code: 'err_invalid_profile' });
      }

      const systems = resolveSystems(_res.subSystems, states.systems);
      // 修正只有一个外链系统的问题，删除对page的判断
      if (!systems.length) {
        clear();
        throw new XomError({ code: 'err_empty_permission' });
      }

      const modules = resolveModules(pages);
      systems.push(...MICROS);
      Object.assign(states, { sites, systems, modules, features });
    };

    const clear = () => {
      const systems = states.systems.filter((s) => !!s.devEntry);
      Object.assign(states, { matched: false, sites: [], systems, modules: [], features: [] });
    };

    return {
      ...toRefs(states),
      authed,
      reload,
      clear,
      markMatched,
      regDevEntry,
      rmvDevEntry,
    };
  },
  {
    persist: {
      key: STORE_PERMIT,
      pick: ['sites', 'systems', 'modules', 'features'],
    },
  },
);

function isSystemPermit<P extends VoPermitSystem>(payload: P): payload is P {
  const { key, path, activeRule, nodeName, systemType } = payload;
  if (!isMacroCase(key) || isEmpty(nodeName) || (systemType !== 'MAIN' && !isUrl(path))) {
    return false;
  }
  return systemType === 'EXTERNAL' || /^\/[\w-]+$/.test(activeRule);
}

function resolveModules(pages: VoPermitModule[]) {
  if (!isArray(pages) || !pages.length) {
    return [];
  }
  return pages.map(({ nodeName: label, pageType, isExternal, ...p }) => {
    const type = isExternal ? 'EXTERNAL' : pageType || 'SUBSYSTEM';
    return {
      ...p,
      type,
      label,
      sort: Number(p.sort),
      path: isExternal || pageType === 'EXTERNAL' ? replaceModuleExternalEntry(p.path) : p.path,
    };
  });
}

function resolveSystems(vos: VoPermitSystem[], olds: XomStateSystem[]): XomStateSystem[] {
  if (!isArray(vos) || !vos.length) {
    return [];
  }

  const invalid: VoPermitSystem[] = [];
  const truly: XomStateSystem[] = [];

  for (const vo of vos) {
    const raw = parseVoToSystem(vo);
    const old = olds.find((o_) => o_.activeRule === raw.activeRule);
    if (old) {
      Object.assign(raw, { devEntry: old.devEntry });
    }
    if (raw) {
      truly.push(raw);
    } else {
      invalid.push(vo);
    }
  }

  if (invalid.length) {
    useSystemInvalidTip(invalid);
  }

  return truly;
}

/**
 * 将接口获取到的子系统配置转换为可渲染配置
 *
 * @param vo - 子系统值
 */
function parseVoToSystem(vo: VoPermitSystem): XomStateSystem {
  if (!isSystemPermit(vo)) {
    return;
  }
  if (
    !vo.devEntry &&
    /^((https?:)\/{2}((?:\d+\.){3}\d+)(:\d+)?)/.test(vo.path) &&
    !(IS_JYH_PROD || IS_JYH_RESERVE)
  ) {
    return;
  }

  const { nodeName: label } = vo;
  const type = vo.isExternal ? 'EXTERNAL' : vo.systemType || 'SUBSYSTEM';
  const container = `#${SYS_MOUNT_POINT}`;
  const entry = replaceSystemEntry(vo.path);

  return { ...pick(vo, ['key', 'activeRule', 'devEntry']), type, container, label, entry };
}

function useSystemInvalidTip(systems: VoPermitSystem[]) {
  const title = createVNode('p', null, `下列子系统含无效配置，它们将被基座忽略:`);
  const list = systems.map((info) =>
    createVNode(
      'span',
      { style: 'display: inline-block' },
      `${info.nodeName}(${info.key}@"${info.activeRule}"): ${info.path}`,
    ),
  );

  useConfirm('error', {
    title: '子系统配置无效',
    content: createVNode('div', null, [title, createVNode('p', null, list)]),
  });
}

/**
 * 替换子系统入口
 *
 * @remarks
 *
 * 华龙芯、党群集约化，两大环境存在一套后端接口、两套/多套前端部署的情况，
 * 在这种情况下，由于同源策略的影响，后端返回的子系统入口，必然汁有一个环境能成功挂载，其它环境则无法挂载，
 * 此函数则用于将这些环境的子系统入口动态替换为基座同域名下的部署入口
 *
 * @example
 *
 * 预发布环境子系统入口 hlxmain.cqnews.net 替换为 hlxmain2.cqnews.net
 *
 * @todo 搭建配置中心，移除此转换函数
 */
function replaceSystemEntry(url: string) {
  const { variable: VITE_HLX_BASE } = resolveEnvironmentVariables('HLX_BASE');
  const HLX_BASE = getBaseConfigByOrigin(VITE_HLX_BASE);
  if (IS_JYH_PROD || IS_JYH_RESERVE) {
    return url.replace(/((https?:)\/\/[^/]+)(.*)/, `${HLX_BASE}$3`);
  } else if (IS_RESERVE) {
    return url.replace(/^(https?:\/\/)(hlx|hlxmain)(\.cqnews\.net\/sub-systems-.*)/, '$1$22$3');
  } else {
    return url;
  }
}

// 替换外链地址
function replaceModuleExternalEntry(path: string) {
  // 匹配完整的HTTP/HTTPS URL或协议相对URL（以//开头）
  const isUrl = path.match(/^(https?:)?\/\//);

  if (!isUrl) {
    // 如果不是完整URL，直接返回
    return path;
  }
  const { variable: VITE_HLX_BASE } = resolveEnvironmentVariables('HLX_BASE');
  const HLX_BASE = getBaseConfigByOrigin(VITE_HLX_BASE);
  return IS_JYH_PROD || IS_JYH_RESERVE
    ? path.replace(/((https?:)?\/\/[^/]+)(.*)/, `${HLX_BASE}$3`)
    : path;
}
